import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  SelectChangeEvent,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check, InfoOutlined } from '@mui/icons-material';
import SurveyContactData from '../../../../components/SurveyContactData/SurveyContactData';
import DesignChanger from '../../../../components/DesignChanger/DesignChanger';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import { IContactData } from '../../../../shared/interfaces/IContactData';
import SurveyService from '../../../../services/SurveyService';
import { AuthContext } from '../../../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { ICustomSurveyEntry } from '../../../../shared/interfaces/ICustomSurveyEntry';
import { ISurvey } from '../../../../shared/interfaces/ISurvey';
import SurveyUtils from '../../../../shared/SurveyUtils';
import StringUtils from '../../../../shared/StringUtils';
import PlatformSelect from '../../../../components/PlatformSelect/PlatformSelect';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import PlatformService from '../../../../services/PlatformService';

const defaultDesign: IDesign = {
  companyLogo: '',
  customFont: '',
  customFontFormat: '',
  customCss: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
};

const defaultContactData = {
  contactEmail: '',
  imprintLink: '',
  privacyPolicyLink: '',
  logoLink: '',
};

const defaultSurveyData: ICustomSurveyEntry = {
  id: '',
  archived: false,
  archivedAt: '',
  createdAt: '',
  updatedAt: '',
  companyName: '',
  companyWebsiteUrl: '',
  contactForm: false,
  customFont: '',
  customFontFormat: '',
  customCss: '',
  label: '',
  lang: 'de',
  note: '',
  outroText: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  outroTextNegative: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  slug: '',
  title: 'Individuelle Umfrage',
  introText: 'Willkommen zu unserer Umfrage!',
  type: 'CUSTOMSURVEY',
  progressBar: false,
  platform: '',
  platformUrl: '',
  introDisabled: false,
  outroDisabled: false,
  i18n: {},
};

interface Props {
  id?: string;
  loadSurveyType?: string;
  isEdit?: boolean;
  reload: () => Promise<void>;
  hasAnswers: boolean;
}

const CustomSurveyGuid: FC<Props> = ({ id, isEdit, reload, loadSurveyType, hasAnswers }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [loading, setLoading] = useState<boolean>(false);

  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState<number>(0);
  const [canEditDesign, setCanEditDesign] = useState<boolean>(false);
  const [showContactData, setShowContactData] = useState<boolean>(defaultSurveyData.contactForm);
  const [initialized, setInitialized] = useState<boolean>(false);

  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [contactData, setContactData] = useState<IContactData>(defaultContactData);
  const [surveyData, setSurveyData] = useState<ICustomSurveyEntry>(defaultSurveyData);
  const [showUrlError, setShowUrlError] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [surveyID, setSurveyID] = useState<string>(id || '');

  const [platforms, setPlatforms] = useState<IPlatform[]>([]);

  console.log('CustomSUrveyGuid');

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async (signal: AbortSignal) => {
      const platforms = await PlatformService.getPlatforms(setLoading, signal);
      if (!platforms) return;

      setPlatforms([...platforms, PlatformService.getOthersPseudoPlatform(t)]);
    };

    (async () => await getPlatforms(controller.signal))();

    return () => {
      controller.abort();
    };
  }, []);
  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const getMergedState = (designOverride: IDesign = design): ISurvey =>
    ({
      ...surveyData,
      ...contactData,
      ...designOverride,
    } as unknown as ISurvey);
  const handleAddSurvey = async () => {
    const mergedState: any = getMergedState();
    if (isEdit) {
      if (!id) return;
      await SurveyService.updateCustomsurvey(id, mergedState);
    } else {
      mergedState.slug = '';
      await SurveyService.addCustomsurvey(mergedState);
    }
    await reload();
  };

  const handleOutroTextChange = (companyName: string) => {
    const value = surveyData.outroText.replace(/\[Firmenname\]/, companyName || '[Firmenname]');
    setSurveyData((prevState) => ({
      ...prevState,
      outroText: value,
    }));
  };

  useEffect(() => {
    if (!hasRole('can-use-customsurvey')) {
      //navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    if (!surveyID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurvey = async () => {
      setLoading(true);
      try {
        const endpointPath = SurveyUtils.getEndpointByType(loadSurveyType);
        const survey = await SurveyService.getSurveyPublic(
          surveyID,
          endpointPath,
          setLoading,
          signal
        );
        if (!survey) {
          setLoading(false);
          return;
        }
        if (survey.type === 'CUSTOMSURVEY') {
          setSurveyData(survey);
        }
        setDesign({
          companyLogo: survey.companyLogo,
          primaryColor: survey.primaryColor,
          secondaryColor: survey.secondaryColor,
          customFont: survey.customFont,
          customFontFormat: survey.customFontFormat,
          customCss: survey.customCss ?? '',
        });
        setContactData({
          privacyPolicyLink: survey.privacyPolicyLink,
          contactEmail: survey.contactEmail,
          imprintLink: survey.imprintLink,
          logoLink: survey.logoLink,
        });
        setIsCheckboxChecked(survey.feedbackActive);
        setShowContactData(
          survey.imprintLink !== '' || survey.contactEmail !== '' || survey.privacyPolicyLink !== ''
        );
        setCanEditDesign(
          survey.primaryColor !== '#357E8B' ||
            survey.secondaryColor !== '#000000' ||
            survey.companyLogo !== '' ||
            !!survey.customCss
        );
      } catch (error) {
        console.error('Failed to fetch survey:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurvey();

    return () => {
      controller.abort();
    };
  }, [surveyID]);

  const shouldRender = (stepId: string) => {
    const ALWAYS_EDITABLE = ['survey_label', 'survey_title', 'intro', 'outro', 'progress_bar'];
    const RENDER_WHEN_FRESH: string[] = ['portal_data'];

    if (!isEdit) {
      return true;
    }

    if (ALWAYS_EDITABLE.includes(stepId)) {
      return true;
    }

    if (!hasAnswers && RENDER_WHEN_FRESH.includes(stepId)) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Box sx={{ maxWidth: '600px' }}>
        <pre></pre>
        <Stepper
          activeStep={currentSurveyGuideStep}
          orientation="vertical"
        >
          {shouldRender('survey_label') && (
            <Step>
              <StepLabel>{t('survey_label')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('survey_name')}
                  placeholder={'z.B.: Musterfirma'}
                  size={'small'}
                  variant="outlined"
                  sx={{ minWidth: '400px' }}
                  autoFocus
                  value={surveyData.label}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      label: e.target.value,
                    }));
                  }}
                />
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Button
                    disabled={!surveyData.label}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('company_data_label') && (
            <Step>
              <StepLabel>{t('company_data_label')}</StepLabel>
              <StepContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 284,
                  }}
                >
                  <TextField
                    label={t('company_name')}
                    size={'small'}
                    variant="outlined"
                    value={surveyData.companyName}
                    placeholder={t('company_name')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        companyName: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={t('enter_company_url')}
                    size="small"
                    variant="outlined"
                    placeholder={'z.B.: example.com'}
                    multiline
                    maxRows={'2'}
                    value={surveyData.companyWebsiteUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        companyWebsiteUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{
                      minWidth: '284px',
                      mt: theme.spacing(2),
                      color: showUrlError ? 'red' : 'inherit',
                    }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    disabled={
                      !surveyData.companyName || !surveyData.companyWebsiteUrl || showUrlError
                    }
                    variant="contained"
                    onClick={() => {
                      if (!StringUtils.isValidCompanyUrl(surveyData.companyWebsiteUrl)) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                      handleOutroTextChange(surveyData.companyName);
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}

          {shouldRender('survey_title') && (
            <Step>
              <StepLabel>{t('title')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('title')}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.title}
                  placeholder={'Bitte teilen Sie uns Ihre aktuelle Stimmung mit.'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      title: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('intro') && (
            <Step>
              <StepLabel>{'Intro'}</StepLabel>
              <StepContent>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!surveyData.introDisabled}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setSurveyData((prevState) => ({
                            ...prevState,
                            introDisabled: !prevState.introDisabled,
                          }));
                        }}
                      ></Checkbox>
                    }
                    label={t('enable_intro')}
                  />
                </Box>
                <TextField
                  label={t('intro')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={surveyData.introText}
                  disabled={surveyData.introDisabled}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      introText: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('outro') && (
            <Step>
              <StepLabel>{'Outro'}</StepLabel>
              <StepContent>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!surveyData.outroDisabled}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setSurveyData((prevState) => ({
                            ...prevState,
                            outroDisabled: !prevState.outroDisabled,
                          }));
                        }}
                      ></Checkbox>
                    }
                    label={t('enable_outro')}
                  />
                </Box>
                <TextField
                  label={t('outro_positive')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={surveyData.outroText}
                  disabled={surveyData.outroDisabled}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      outroText: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={t('outro_negative')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={surveyData.outroTextNegative}
                  disabled={surveyData.outroDisabled}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      outroTextNegative: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('portal_data') && (
            <Step>
              <StepLabel>
                <Tooltip
                  title={t('portal_data_tooltip')}
                  placement="top"
                  arrow
                >
                  <span>
                    {`${t('portal_data')} (${t('optional_configuration')})`}
                    <InfoOutlined
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                  </span>
                </Tooltip>
              </StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <PlatformSelect
                    value={surveyData.platform}
                    onChange={(event: SelectChangeEvent) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        platform: event.target.value,
                      }));
                    }}
                    platforms={platforms}
                  />
                  <TextField
                    label={`${t('portal_data')}-URL`}
                    size="small"
                    variant="outlined"
                    placeholder={'z.B.: https://www.kununu.de/bewertungen'}
                    value={surveyData.platformUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        platformUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (
                        !StringUtils.isValidPlatformUrl(surveyData.platformUrl) &&
                        surveyData.platformUrl !== ''
                      ) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('progress_bar') && (
            <Step>
              <StepLabel>{t('progress_bar')}</StepLabel>
              <StepContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={surveyData.progressBar}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          progressBar: !prevState.progressBar,
                        }));
                      }}
                    ></Checkbox>
                  }
                  label={t('enable_progress_bar')}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          <Step>
            <StepLabel>{t('finish_configuration')}</StepLabel>
            <StepContent>
              <Box sx={{ mb: theme.spacing(2) }}>
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  onClick={handleAddSurvey}
                >
                  {t('finish')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handlePreviousSurveyGuideStep}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                >
                  {t('back')}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      {!isEdit ? (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: theme.spacing(1),
              }}
            >
              <Typography>{t('language')}</Typography>
              <ToggleButtonGroup
                color="primary"
                size="small"
                value={surveyData.lang}
                exclusive
                onChange={(event: MouseEvent<HTMLElement>, value: string) => {
                  if (value) {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      lang: value,
                    }));
                  }
                }}
                sx={{
                  '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                }}
              >
                <ToggleButton value="de">
                  <FlagDe
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
                <ToggleButton value="en">
                  <FlagGb
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />
                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="CUSTOMSURVEY"
                    buildPreviewSurvey={getMergedState}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
                logoLink={contactData.logoLink}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  {['de', 'en'].includes(surveyData.lang) ? (
                    <>
                      <ToggleButtonGroup
                        color="primary"
                        size="small"
                        value={surveyData.lang}
                        disabled={!['de', 'en'].includes(surveyData.lang)}
                        exclusive
                        onChange={(event: MouseEvent<HTMLElement>, value: string | null) => {
                          if (value) {
                            setSurveyData((prevState) => ({
                              ...prevState,
                              lang: value,
                            }));
                          }
                        }}
                        sx={{
                          '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                            border: `1px solid ${theme.palette.primary.main}`,
                          },
                        }}
                      >
                        <ToggleButton value="de">
                          <FlagDe
                            width="32px"
                            height="100%"
                          />
                        </ToggleButton>
                        <ToggleButton value="en">
                          <FlagGb
                            width="32px"
                            height="100%"
                          />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </>
                  ) : (
                    <Typography>{t('language_set_elsewhere')}</Typography>
                  )}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="CUSTOMSURVEY"
                    buildPreviewSurvey={getMergedState}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
                logoLink={contactData.logoLink}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default CustomSurveyGuid;
